// =============================================================================
// FILE: packages/renderer/src/lib/soundManager.ts
// PURPOSE: Singleton Web Audio click synthesiser. Generates a crisp mechanical
//          "instrument panel" click without any external audio files.
//          Uses the AudioContext API — gracefully degrades in environments
//          that don't support it (Electron renderer, desktop Chrome both work).
// =============================================================================

let _ctx: AudioContext | null = null;
let _enabled = true;

/** Lazily create (or resume) the shared AudioContext. */
function getCtx(): AudioContext | null {
  if (!_enabled) return null;
  if (typeof AudioContext === 'undefined') return null;
  try {
    if (!_ctx) _ctx = new AudioContext();
    if (_ctx.state === 'suspended') void _ctx.resume();
    return _ctx;
  } catch {
    return null;
  }
}

/**
 * Synthesises a high-end mechanical click:
 *  - Brief filtered noise burst for the "tick" transient
 *  - Low-frequency thump for body / presence
 * Total duration ≈ 25ms — inaudible latency, tactile feel.
 */
export function playClick(volume = 0.08): void {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;

    // ── Noise tick ──────────────────────────────────────────────────────────
    const bufLen = Math.ceil(ctx.sampleRate * 0.014);
    const buf    = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const data   = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) {
      const env  = Math.pow(1 - i / bufLen, 2.4);   // snappy cubic decay
      data[i]    = (Math.random() * 2 - 1) * env;
    }
    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = buf;

    const hpf = ctx.createBiquadFilter();
    hpf.type            = 'highpass';
    hpf.frequency.value = 2600;
    hpf.Q.value         = 0.7;

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(volume, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.022);

    noiseSrc.connect(hpf);
    hpf.connect(noiseGain);
    noiseGain.connect(ctx.destination);
    noiseSrc.start(now);
    noiseSrc.stop(now + 0.025);

    // ── Low thump ───────────────────────────────────────────────────────────
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, now);
    osc.frequency.exponentialRampToValueAtTime(55, now + 0.016);

    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(volume * 0.55, now);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.020);

    osc.connect(thumpGain);
    thumpGain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.022);
  } catch {
    // AudioContext errors are non-fatal
  }
}

/** Softer variant for navigation / tab switches. */
export function playNav(): void {
  playClick(0.055);
}

/**
 * Medical-monitor "beep" — a clean sine tone with a quick envelope. Used by the
 * heartbeat/ECG monitor when the operator taps it to freeze/resume the trace.
 */
export function playBeep(freq = 880, volume = 0.12): void {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.18);
  } catch {
    // non-fatal
  }
}

/**
 * Deep "Engine Hum" — a powerful, layered low-end swell for the Warp Drive
 * take-off. Two detuned saw oscillators through a rising low-pass filter give a
 * spaceship-engine spool-up you can feel. Duration ≈ 1.6s.
 */
export function playEngineHum(): void {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    const now = ctx.currentTime;
    const dur = 1.6;

    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.22, now + 0.25);   // spool up
    master.gain.setValueAtTime(0.22, now + dur - 0.5);
    master.gain.exponentialRampToValueAtTime(0.0001, now + dur);  // fade out

    const lpf = ctx.createBiquadFilter();
    lpf.type = 'lowpass';
    lpf.frequency.setValueAtTime(180, now);
    lpf.frequency.exponentialRampToValueAtTime(900, now + dur * 0.7);
    lpf.Q.value = 6;

    const freqs = [42, 63, 84];   // fundamental + harmonics
    const detunes = [0, 7, -6];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(f, now);
      osc.frequency.exponentialRampToValueAtTime(f * 1.5, now + dur * 0.8);  // rising pitch = acceleration
      osc.detune.value = detunes[i] ?? 0;
      const g = ctx.createGain();
      g.gain.value = i === 0 ? 0.9 : 0.4;
      osc.connect(g); g.connect(lpf);
      osc.start(now);
      osc.stop(now + dur);
    });

    // sub-bass thump for the "launch" kick
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(50, now);
    sub.frequency.exponentialRampToValueAtTime(28, now + 0.4);
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(0.5, now);
    subGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.5);
    sub.connect(subGain); subGain.connect(ctx.destination);
    sub.start(now); sub.stop(now + 0.5);

    lpf.connect(master);
    master.connect(ctx.destination);
  } catch {
    // non-fatal
  }
}

/** Fire an iPhone-style haptic tap (no-op where unsupported). */
export function haptic(pattern: number | number[] = 12): void {
  try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(pattern); } catch { /* */ }
}

/** Toggle mute globally (persisted to localStorage). */
export function setSoundEnabled(on: boolean): void {
  _enabled = on;
  try { localStorage.setItem('rcd2.sound', on ? '1' : '0'); } catch { /* */ }
}

export function isSoundEnabled(): boolean { return _enabled; }

/** Restore persisted mute state — call once on app boot. */
export function restoreSound(): void {
  try {
    const stored = localStorage.getItem('rcd2.sound');
    if (stored === '0') _enabled = false;
  } catch { /* */ }
}
